import {
  Page,
  PageHeader,
  PageHeaderTitle,
  PageBody,
  DataTable,
  Icon,
  FormattedRelativeTime,
  Box,
  Button,
  PageHeaderActions,
  ButtonGroup,
} from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import { Helmet } from 'react-helmet-async';
import { createColumnHelper } from '@tanstack/table-core';
import { useQueryState, parseAsStringLiteral } from 'nuqs';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import type { BackfillComputed } from '@/features/tasks';
import { useTasks, TaskStateBadge } from '@/features/tasks';
import { TaskObject } from './components/task-object';
import { TaskExpandedRow } from './components/task-expanded-row';
import { isEmptyString } from '@/utils/string';
import { type BackfillState, backfillStates } from '@/services/api/services-api';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { BACKFILL_TYPES, type BackfillType } from '@/features/tasks/utils/task-types';
import { isInvisibleLabel } from '@/features/tasks/utils/task-types';
import { useMemo } from 'react';
import { STATE_LABELS } from '@/features/tasks/components/task-state-badge';

const columnHelper = createColumnHelper<BackfillComputed>();

const columns = [
  columnHelper.display({
    id: 'icon',
    header: '',
    cell: (info) => {
      const backfill = info.row.original;
      return (
        <Box display="flex" alignItems="center" h="full">
          <Icon name={backfill.info.icon as IconName} />
        </Box>
      );
    },
    meta: {
      width: 44,
    },
  }),
  columnHelper.accessor('info.label', {
    id: 'task',
    header: 'Task',
    cell: (info) => info.getValue(),
    meta: {
      width: '1fr',
    },
  }),
  columnHelper.display({
    id: 'detail',
    header: 'Detail',
    cell: (info) => {
      const backfill = info.row.original;
      return <TaskObject backfill={backfill} />;
    },
    meta: {
      width: '2fr',
    },
  }),
  columnHelper.display({
    id: 'updated',
    header: 'Updated',
    cell: (info) => {
      const backfill = info.row.original;
      const lastUpdate = backfill.completed ?? backfill.started ?? backfill.created;

      return <FormattedRelativeTime date={new Date(lastUpdate * 1000)} size="s" color="subtle" />;
    },
    meta: {
      align: 'end',
    },
  }),
  columnHelper.accessor('state', {
    id: 'status',
    header: 'Status',
    cell: (info) => <TaskStateBadge state={info.getValue()} />,
    meta: {
      align: 'end',
    },
  }),
];

const emptyStateMessages = {
  Success: 'No successful tasks',
  Failure: 'No failed tasks',
  InProgress: 'No in-progress tasks',
  Queued: 'No queued tasks',
  Retrying: 'No retrying tasks',
  Canceled: 'No canceled tasks',
} as const;

export default function Tasks() {
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const [continuation, setContinuation] = useQueryState('continuation');
  const [expandedId, setExpandedId] = useQueryState('expand');
  const [backfillState, setBackfillState] = useQueryState('state', parseAsStringLiteral<BackfillState>(backfillStates));
  const isPerformanceDashboardEnabled = useFeatureFlag(AccountFeatureFlag.TasksPerformanceDashboard);
  const [backfillType, setBackfillType] = useQueryState(
    'type',
    parseAsStringLiteral<BackfillType>(Object.keys(BACKFILL_TYPES) as readonly BackfillType[]),
  );

  const { tasks, meta, isLoading } = useTasks({
    workspaceId,
    state: backfillState,
    continuation,
    type: backfillType,
  });

  const nextContinuation = meta?.continuation ?? '';

  const canAdvance = isEmptyString(nextContinuation) === false;
  const canRestart = isEmptyString(continuation) === false;

  const handleAdvance = () => {
    if (canAdvance === true) {
      void setContinuation(nextContinuation);
    }
  };

  const handleRestart = () => {
    if (canRestart === true) {
      void setContinuation(null);
    }
  };

  const savedViews = useMemo(
    () =>
      isPerformanceDashboardEnabled
        ? {
            defaultViewName: 'All Tasks',
            views: Object.entries(emptyStateMessages).map(([key]) => ({
              id: key,
              name: STATE_LABELS[key as keyof typeof emptyStateMessages],
            })),
            activeViewId: backfillState,
            onActiveViewChange: (viewId: string | null) => {
              void setBackfillState(viewId as BackfillState);
              void setExpandedId(null);
              void setContinuation(null);
              void setBackfillType(null);
            },
          }
        : undefined,
    [backfillState, setBackfillState, setExpandedId, setContinuation, isPerformanceDashboardEnabled, setBackfillType],
  );

  const filtersConfig = useMemo(() => {
    if (!isPerformanceDashboardEnabled) {
      return undefined;
    }

    return {
      filters: [
        {
          key: 'type',
          label: 'Task',
          type: 'radio' as const,
          options: Object.entries(BACKFILL_TYPES)
            .filter(([key]) => !isInvisibleLabel(key as BackfillType))
            .map(([key, type]) => ({ label: type.label, value: key }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        },
      ],
      values: backfillType != null ? [{ key: 'type', value: backfillType }] : [],
      onChange: (key: string, value: string) => {
        if (key === 'type') {
          void setBackfillType(value as BackfillType);
          void setContinuation(null);
        }
      },
      onClear: (key: string) => {
        if (key === 'type') {
          void setBackfillType(null);
          void setContinuation(null);
        }
      },
      onClearAll: () => {
        void setContinuation(null);
        void setBackfillType(null);
      },
    };
  }, [backfillType, setBackfillType, setContinuation, isPerformanceDashboardEnabled]);

  return (
    <>
      <Helmet>
        <title>Tasks | Customer.io</title>
      </Helmet>
      <Page w="full">
        <PageHeader>
          <PageHeaderTitle>Tasks</PageHeaderTitle>
          <PageHeaderActions>
            <ButtonGroup variant="tertiary" size="small">
              <Button icon="chevron-left" isDisabled={!canRestart} onClick={handleRestart} isIconOnly={true} />
              <Button icon="chevron-right" isDisabled={!canAdvance} onClick={handleAdvance} isIconOnly={true} />
            </ButtonGroup>
          </PageHeaderActions>
        </PageHeader>
        <PageBody>
          <DataTable
            caption="Background tasks"
            data={tasks}
            columns={columns}
            withRowExpanding={true}
            expandedRowComponent={TaskExpandedRow}
            initiallyExpandedRows={isEmptyString(expandedId) === false ? { [expandedId]: true } : {}}
            getRowId={(row: BackfillComputed) => String(row.id)}
            isLoading={isLoading}
            emptyState={{
              title: backfillState != null ? emptyStateMessages[backfillState] : 'No Tasks',
              icon: 'notifications',
              description: backfillState == null ? 'Try sending a newsletter or building a segment!' : undefined,
            }}
            withSavedViews={savedViews != null}
            savedViews={savedViews}
            withFilters={filtersConfig != null}
            filters={filtersConfig}
          />
        </PageBody>
      </Page>
    </>
  );
}
